import React, { useState } from 'react';
import {
  Pill,
  Search,
  Clock,
  AlertCircle,
  CheckCircle,
  MoreVertical,
  Download,
  RefreshCw
} from 'lucide-react';
import { format, parseISO, isValid, differenceInDays } from 'date-fns';
import { useMedications } from '../hooks/use-medications';
import { Medication } from '../types/medication-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import GlassCard from '@/components/ui/glass-card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Alert, AlertDescription } from "@/components/ui/alert";
import Loading from '@/components/ui/Loading';
import ErrorMessage from '@/components/ui/ErrorMessage';
import { EmptyState } from '@/components/ui/EmptyState';
import PageContainer from '@/components/layout/PageContainer';
import ErrorBoundary from '@/components/ui/ErrorBoundary';

/**
 * Medications Page Component
 * Displays current medications, medication history, refill requests, and medication reminders
 */
const Medications: React.FC = () => {
  const { medications, isLoading, isError, error } = useMedications();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTab, setSelectedTab] = useState<'current' | 'history' | 'refills'>('current');

  // Filter medications by search term
  const filteredMedications = React.useMemo(() => {
    if (!medications) return [];

    const filtered = medications.filter(med => {
      const searchLower = searchTerm.toLowerCase();
      return (
        med.name?.toLowerCase().includes(searchLower) ||
        med.dosage?.toLowerCase().includes(searchLower) ||
        med.notes?.toLowerCase().includes(searchLower)
      );
    });

    // Filter by active status based on tab
    if (selectedTab === 'current') {
      return filtered.filter(med => med.status === 'active');
    } else if (selectedTab === 'history') {
      return filtered.filter(med => med.status !== 'active');
    }

    return filtered;
  }, [medications, searchTerm, selectedTab]);

  // Get status badge
  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { variant: "default" | "secondary" | "destructive" | "outline" | "success" | "warning", label: string }> = {
      'active': { variant: 'success', label: 'Active' },
      'discontinued': { variant: 'secondary', label: 'Discontinued' },
      'completed': { variant: 'outline', label: 'Completed' },
      'on-hold': { variant: 'warning', label: 'On Hold' },
    };

    const statusConfig = statusMap[status] || { variant: 'default' as const, label: status };

    return (
      <Badge variant={statusConfig.variant} className="capitalize">
        {statusConfig.label}
      </Badge>
    );
  };

  // Format date safely
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    try {
      const date = parseISO(dateString);
      return isValid(date) ? format(date, 'MMM d, yyyy') : 'Invalid Date';
    } catch {
      return 'Invalid Date';
    }
  };

  // Calculate days remaining for medication
  const getDaysRemaining = (endDate: string | null | undefined) => {
    if (!endDate) return null;
    try {
      const end = parseISO(endDate);
      if (!isValid(end)) return null;
      const today = new Date();
      const days = differenceInDays(end, today);
      return days > 0 ? days : 0;
    } catch {
      return null;
    }
  };

  // Handle export medications
  const handleExport = () => {
    const csvContent = [
      ['Medication', 'Dosage', 'Frequency', 'Status', 'Start Date', 'End Date', 'Prescriber', 'Notes'].join(','),
      ...filteredMedications.map(med => [
        med.name,
        med.dosage,
        med.frequency,
        med.status,
        formatDate(med.startDate),
        formatDate(med.endDate),
        med.prescribedBy || 'N/A',
        `"${med.notes || ''}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `medications-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <PageContainer
      title="Medications"
      description="View and manage your medications"
      icon={<Pill className="h-5 w-5" />}
      actions={
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      }
    >
      <ErrorBoundary>
        {/* Active Medications Alert */}
        {medications && medications.filter(m => m.status === 'active').length > 0 && (
          <Alert className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              You have {medications.filter(m => m.status === 'active').length} active medication(s).
              Please take them as prescribed.
            </AlertDescription>
          </Alert>
        )}

        {/* Search and Filter */}
        <GlassCard padding="sm" variant="default" className="mb-6 shadow-sm border-white/40">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500 h-4 w-4" />
              <Input
                type="text"
                placeholder="Search medications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-white/20 dark:border-white/10 w-full bg-white/50 dark:bg-slate-800/50 shadow-sm focus-visible:ring-1 focus-visible:ring-[var(--brand-primary)]"
              />
            </div>
          </div>
        </GlassCard>

        {/* Tabs for Current, History, and Refills */}
        <Tabs value={selectedTab} onValueChange={(val) => setSelectedTab(val as typeof selectedTab)}>
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="current">
              <CheckCircle className="h-4 w-4 mr-2" />
              Current
            </TabsTrigger>
            <TabsTrigger value="history">
              <Clock className="h-4 w-4 mr-2" />
              History
            </TabsTrigger>
            <TabsTrigger value="refills">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refills
            </TabsTrigger>
          </TabsList>

          {/* Current Medications */}
          <TabsContent value="current">
            <GlassCard padding="none" variant="default" className="shadow-sm border-white/40 overflow-hidden">
              <div className="p-6 border-b border-white/20 dark:border-white/10 bg-white/40 dark:bg-slate-800/40">
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Current Medications</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  Medications you are currently taking
                </p>
              </div>
              <div className="p-6">
                <ErrorBoundary>
                  {isLoading ? (
                    <Loading text="Loading medications..." />
                  ) : isError ? (
                    <ErrorMessage message={error?.message || "Failed to load medications."} />
                  ) : filteredMedications.length === 0 ? (
                    <EmptyState
                      title="No Current Medications"
                      message={searchTerm ? "Try adjusting your search." : "You don't have any active medications."}
                      className="py-10"
                    />
                  ) : (
                    <div className="space-y-4">
                      {filteredMedications.map((medication) => (
                        <MedicationCard
                          key={medication.id}
                          medication={medication}
                          formatDate={formatDate}
                          getDaysRemaining={getDaysRemaining}
                          getStatusBadge={getStatusBadge}
                        />
                      ))}
                    </div>
                  )}
                </ErrorBoundary>
              </div>
            </GlassCard>
          </TabsContent>

          {/* Medication History */}
          <TabsContent value="history">
            <GlassCard padding="none" variant="default" className="shadow-sm border-white/40 overflow-hidden">
              <div className="p-6 border-b border-white/20 dark:border-white/10 bg-white/40 dark:bg-slate-800/40">
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Medication History</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  Previous and discontinued medications
                </p>
              </div>
              <div className="p-6">
                <ErrorBoundary>
                  {isLoading ? (
                    <Loading text="Loading medication history..." />
                  ) : isError ? (
                    <ErrorMessage message={error?.message || "Failed to load medication history."} />
                  ) : filteredMedications.length === 0 ? (
                    <EmptyState
                      title="No Medication History"
                      message={searchTerm ? "Try adjusting your search." : "You don't have any discontinued medications."}
                      className="py-10"
                    />
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Medication</TableHead>
                          <TableHead>Dosage</TableHead>
                          <TableHead>Duration</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredMedications.map((med) => (
                          <TableRow key={med.id}>
                            <TableCell className="font-medium">{med.name}</TableCell>
                            <TableCell>{med.dosage}</TableCell>
                            <TableCell>
                              {formatDate(med.startDate)} - {formatDate(med.endDate)}
                            </TableCell>
                            <TableCell>{getStatusBadge(med.status)}</TableCell>
                            <TableCell>
                              <Button variant="ghost" size="sm">
                                View Details
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </ErrorBoundary>
              </div>
            </GlassCard>
          </TabsContent>

          {/* Refill Requests */}
          <TabsContent value="refills">
            <GlassCard padding="none" variant="default" className="shadow-sm border-white/40 overflow-hidden">
              <div className="p-6 border-b border-white/20 dark:border-white/10 bg-white/40 dark:bg-slate-800/40">
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Refill Requests</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  Request refills for your medications
                </p>
              </div>
              <div className="p-6">
                <ErrorBoundary>
                  <EmptyState
                    title="Refill Requests"
                    message="Refill request functionality coming soon. Contact your doctor directly for now."
                    className="py-10"
                  />
                </ErrorBoundary>
              </div>
            </GlassCard>
          </TabsContent>
        </Tabs>
      </ErrorBoundary>
    </PageContainer>
  );
};

/**
 * Medication Card Component
 * Displays individual medication information
 */
interface MedicationCardProps {
  medication: Medication;
  formatDate: (date: string | null | undefined) => string;
  getDaysRemaining: (date: string | null | undefined) => number | null;
  getStatusBadge: (status: string) => JSX.Element;
}

const MedicationCard: React.FC<MedicationCardProps> = ({
  medication,
  formatDate,
  getDaysRemaining,
  getStatusBadge
}) => {
  const daysRemaining = getDaysRemaining(medication.endDate);

  return (
    <GlassCard padding="lg" variant="subtle" className="border-white/40 hover:shadow-md transition-all duration-300 group hover:translate-y-[-2px]">
      <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="text-lg font-semibold">{medication.name}</h3>
                <p className="text-sm text-muted-foreground">{medication.dosage} - {medication.frequency}</p>
              </div>
              <div className="flex gap-2 items-center">
                {getStatusBadge(medication.status)}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem>View Details</DropdownMenuItem>
                    <DropdownMenuItem>Request Refill</DropdownMenuItem>
                    <DropdownMenuItem>Set Reminder</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              <div>
                <p className="text-xs text-muted-foreground">Start Date</p>
                <p className="text-sm font-medium">{formatDate(medication.startDate)}</p>
              </div>

              {medication.endDate && (
                <div>
                  <p className="text-xs text-muted-foreground">End Date</p>
                  <p className="text-sm font-medium">
                    {formatDate(medication.endDate)}
                    {daysRemaining !== null && daysRemaining > 0 && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        ({daysRemaining} days remaining)
                      </span>
                    )}
                  </p>
                </div>
              )}

              {medication.prescribedBy && (
                <div>
                  <p className="text-xs text-muted-foreground">Prescribed By</p>
                  <p className="text-sm font-medium">{medication.prescribedBy}</p>
                </div>
              )}

              {medication.prescribedDate && (
                <div>
                  <p className="text-xs text-muted-foreground">Prescribed Date</p>
                  <p className="text-sm font-medium">{formatDate(medication.prescribedDate)}</p>
                </div>
              )}
            </div>

            {medication.notes && (
              <div className="mt-4 p-3 bg-muted rounded-md">
                <p className="text-xs text-muted-foreground mb-1">Instructions</p>
                <p className="text-sm">{medication.notes}</p>
              </div>
            )}

            {daysRemaining !== null && daysRemaining <= 7 && daysRemaining > 0 && (
              <Alert className="mt-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  This medication will expire in {daysRemaining} day{daysRemaining !== 1 ? 's' : ''}.
                  Consider requesting a refill.
                </AlertDescription>
              </Alert>
            )}
          </div>
      </div>
    </GlassCard>
  );
};

export default Medications;
